import React from "react";
import axios from "axios";
import axiosRetry from "axios-retry";
import getConfig from "next/config";
import { captureException } from "@sentry/nextjs";
import { Advertisement } from "../../lib/types";

axiosRetry(axios);

const {
  publicRuntimeConfig: { buzzerApiUrl },
} = getConfig();

export type AdvertisementEvent = {
  data: Advertisement;
  receipt: string;
  advertisementId: string;
};

const useAdvertisements = () => {
  const [event, setEvent] = React.useState<AdvertisementEvent | null>(null);
  const [visible, setVisible] = React.useState(false);
  const [willDisappear, setWillDisappear] = React.useState(false);

  const showAdvertisement = React.useCallback(
    (receivedEvent: AdvertisementEvent) => {
      console.log("showAdvertisement");

      if (window.busy) return;
      window.busy = true;
      window.advertisementEvent = receivedEvent;

      setEvent(receivedEvent);

      axios
        .post(
          `${buzzerApiUrl}/advertisements/${receivedEvent.advertisementId}/start`,
          {},
          {
            headers: {
              "x-receipt": window.advertisementEvent.receipt,
            },
          },
        )
        .catch((e) => {
          captureException(e);
        });
    },
    [],
  );

  const hideAdvertisement = React.useCallback(() => {
    console.log("hideAdvertisement");

    if (!window.advertisementEvent) return;

    if (window.advertisementDispose) window.advertisementDispose();
    window.advertisementDispose = undefined;

    setWillDisappear(true);

    axios
      .post<AdvertisementEvent>(
        `${buzzerApiUrl}/advertisements/${window.advertisementEvent.advertisementId}/confirm`,
        {},
        {
          headers: {
            "x-receipt": window.advertisementEvent.receipt,
          },
        },
      )
      .then((response) => {
        setVisible(false);
        setWillDisappear(false);

        if (response.status === 204) {
          window.busy = false;
          window.advertisementEvent = undefined;

          setEvent(null);
        } else {
          window.busy = true;
          window.advertisementEvent = response.data;

          setEvent(response.data);
        }
      })
      .catch((e) => {
        captureException(e);

        window.busy = false;
        window.advertisementEvent = undefined;

        setVisible(false);
        setWillDisappear(false);
        setEvent(null);
      });
  }, []);

  const playAdvertisement = React.useCallback(() => {
    console.log("playAdvertisement");

    if (!event) return;

    try {
      const advertisement = event.data;

      const timeout = setTimeout(
        () => hideAdvertisement(),
        Number(advertisement.data.duration) * 1_000,
      );

      setVisible(true);

      window.advertisementDispose = () => {
        console.log("Disposing advertisement");
        clearTimeout(timeout);
      };
    } catch (e) {
      console.log(e);
      captureException(e);
      hideAdvertisement();
    }
  }, [event]);

  return {
    advertisement: event?.data,
    identifier: event?.advertisementId,
    visible,
    willDisappear,
    showAdvertisement,
    hideAdvertisement,
    playAdvertisement,
  };
};

export default useAdvertisements;
